import { useState } from 'react';
import { ThumbsUp, ThumbsDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEEDBACK_MILESTONES = [1, 5, 10];

/**
 * Track AI generation count and return whether feedback should be shown.
 * Call this after each successful AI generation.
 * @param {string} type - e.g. 'activity_generation', 'parent_summary', 'report_interpretation'
 * @returns {boolean} whether to show the feedback popup
 */
export function shouldShowFeedback(type) {
  const key = `playfirst_ai_count_${type}`;
  const count = parseInt(localStorage.getItem(key) || '0', 10) + 1;
  localStorage.setItem(key, String(count));
  return FEEDBACK_MILESTONES.includes(count);
}

export default function AIFeedbackPopup({ type, context, onDismiss }) {
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const submitFeedback = async (selectedRating) => {
    const finalRating = selectedRating || rating;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/feedback/ai`, {
        type,
        rating: finalRating,
        comment: comment.trim() || null,
        context: context || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmitted(true);
      toast.success('Thanks for your feedback!');
      setTimeout(() => onDismiss?.(), 1500);
    } catch {
      onDismiss?.();
    }
  };

  if (submitted) {
    return (
      <div className="flex items-center justify-center gap-2 py-3 px-4 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700" data-testid="ai-feedback-thanks">
        Thanks for your feedback!
      </div>
    );
  }

  return (
    <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 space-y-2" data-testid="ai-feedback-popup">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-stone-700">Was this helpful?</p>
        <button onClick={() => onDismiss?.()} className="text-stone-400 hover:text-stone-600 p-0.5" data-testid="ai-feedback-dismiss">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant={rating === 'positive' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setRating('positive'); submitFeedback('positive'); }}
          className="rounded-full h-8 px-3 text-xs"
          data-testid="ai-feedback-positive"
        >
          <ThumbsUp className="w-3.5 h-3.5 mr-1" /> Helpful
        </Button>
        <Button
          variant={rating === 'negative' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setRating('negative')}
          className="rounded-full h-8 px-3 text-xs"
          data-testid="ai-feedback-negative"
        >
          <ThumbsDown className="w-3.5 h-3.5 mr-1" /> Not quite
        </Button>
      </div>
      {rating === 'negative' && (
        <div className="space-y-2">
          <Textarea
            placeholder="What could be improved? (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="text-sm rounded-xl min-h-[60px] resize-none"
            data-testid="ai-feedback-comment"
          />
          <Button size="sm" onClick={() => submitFeedback()} className="rounded-full h-8 text-xs" data-testid="ai-feedback-submit">
            Send feedback
          </Button>
        </div>
      )}
    </div>
  );
}
